import { Component, Input, OnChanges } from '@angular/core';
import { LabelI18nService } from 'src/app/core/_services/label-i18n.service';
import { MatCheckboxChange } from '@angular/material/checkbox';
import * as Highcharts from 'highcharts';
import more from 'highcharts/highcharts-more';
import HC_exporting from 'highcharts/modules/exporting';
import HC_offlineExporting from 'highcharts/modules/offline-exporting';
import { Indicator } from 'src/app/core/_models/indicator';
import { ProjectEcoPro } from 'src/app/core/_models/projectEcoPro';
import { Solution } from 'src/app/core/_models/solution';
import { SolutionEcoPro } from 'src/app/core/_models/solutionEcoPro';
import { Structure } from 'src/app/core/_models/structure';
import { HIGHCHARTS_EXPORTING, HIGHCHARTS_TRANSLATIONS } from 'src/constants/graph.constants';
more(Highcharts);
HC_exporting(Highcharts);
HC_offlineExporting(Highcharts);

let componentContext: any;

@Component({
  selector: 'app-radar-indicators',
  templateUrl: './radar-indicators.component.html',
  styleUrls: ['./radar-indicators.component.scss']
})
export class RadarIndicatorsComponent implements OnChanges {
  @Input() projectData: ProjectEcoPro = {};
  @Input() indicators: Indicator[] = [];
  @Input() isEcoPro: boolean = true;
  @Input() synthese: any;
  structures: Structure[] = [];

  selectedIndicators: Indicator[] = [];
  indexDisplayIndicator: number = -1;

  private defaultApplied = false;
  updateFlag: boolean = false;
  totalRef: number = 0;

  // Highcharts config

  Highcharts: typeof Highcharts = Highcharts;
  chartOptions: Highcharts.Options = {
    chart: {
      polar: true
    },
    lang: HIGHCHARTS_TRANSLATIONS,
    title: {
      text: 'Radar de comparaison des solutions'
    },
    exporting: HIGHCHARTS_EXPORTING,
    colors: ['#7cb5ec', '#434348', '#f7a35c', '#8085e9', '#f15c80', '#e4d354', '#2b908f', '#f45b5b', '#91e8e1'],
    pane: {
      startAngle: 0,
      endAngle: 360
    },
    yAxis: {
      min: 0,
      tickInterval: 20,
      max: 100,
      labels: {
        enabled: true,
        formatter: function () {
          return '-' + (100 - Number(this.value)) + '%';
        }
      }
    },
    tooltip: {
      useHTML: true,
      formatter: function () {
        // return '<table><tr><th colspan="2">{point.x}</th></tr><tr><td>' + this.series.name + ' : </td><td style="text-align: right"><b>' + (Number(this.point.y) - 100).toFixed(2) + '%</b></td></tr></table>';
        if (this.point.y == 100) {
          return '<table><tr><th colspan="2">' + this.series.name + '</th></tr><tr><td style="text-align: center"><b> Maximum </b></td></tr></table>';
        } else {
          return '<table><tr><th colspan="2">' + this.series.name + '</th></tr><tr><td style="text-align: center"><b>' + Highcharts.numberFormat(Number(this.point.y) - 100, 2) + '%</b></td></tr></table>';
        }
      }
    }
  };

  constructor(public labelI18n: LabelI18nService) {
    componentContext = this;
  }

  ngOnChanges() {
    if (!this.defaultApplied && this.indicators.length >= 3 && this.synthese?.length > 0) {
      this.defaultApplied = true;
      this.selectedIndicators = [...this.indicators];
    }
    if (this.selectedIndicators.length >= 3) this.formatDataForGraph();
  }

  isIndicatorSelected(indicator: Indicator): boolean {
    return this.selectedIndicators.some((selectedIndicator) => selectedIndicator.indicatorId == indicator.indicatorId);
  }

  onChangeIndicatorSelect(cb: MatCheckboxChange, indicator: Indicator) {
    this.selectedIndicators = this.indicators.filter((i) => (i.indicatorId == indicator.indicatorId ? cb.checked : this.isIndicatorSelected(i)));
    if (this.selectedIndicators.length >= 3) this.formatDataForGraph();
  }

  calculatTotalCost(values: any) {
    return (
      parseFloat(values.AsphaltAggregates) +
      parseFloat(values.BuildTransportCostIn) +
      parseFloat(values.BuildTransportCostOut) +
      parseFloat(values.InitialTransportCost) +
      parseFloat(values.MaterialCost) +
      parseFloat(values.MaterialMakingCost) +
      parseFloat(values.NaturalAggregates) +
      parseFloat(values.RecycledMaterials) +
      parseFloat(values.ReusedSpoil) +
      parseFloat(values.WorkingCost)
    );
  }

  formatDataForGraph() {
    let series: { type: string; name: string | undefined; data: number[] }[] = [];
    let compareData: number[] = [];
    componentContext.indexDisplayIndicator = -1;

    if (this.isEcoPro) {
      let solutionsData: SolutionEcoPro[] = [];

      this.selectedIndicators.forEach((selectedIndicator, i) => {
        compareData = [];

        this.projectData.solutionsEcoPro?.forEach((solution) => {
          let ceuValueForStructureType = 0;

          if (solution.impactsCEU?.find((impact) => impact.displayName == selectedIndicator.displayName)?.value) {
            ceuValueForStructureType = +solution.impactsCEU?.find((impact) => impact.displayName == selectedIndicator.displayName)?.value!;
          }

          let solutionRef = solutionsData.find((solutionData) => solutionData.solutionEcoProId == solution.solutionEcoProId);

          compareData.push(ceuValueForStructureType);

          if (solutionRef == null) {
            solutionsData.push({
              solutionEcoProId: solution.solutionEcoProId,
              displayName: solution.displayName,
              dataForGraph: [ceuValueForStructureType],
              structureTypesProject: []
            });
          } else {
            solutionRef.dataForGraph?.push(ceuValueForStructureType);
          }
        });

        solutionsData.forEach((solutionData) => {
          if (solutionData.dataForGraph != null) {
            if (solutionData.dataForGraph[i] == Math.max(...compareData)) {
              solutionData.dataForGraph[i] = 100;
            } else {
              solutionData.dataForGraph[i] = 100 + (100 * (solutionData.dataForGraph[i] - Math.max(...compareData))) / Math.max(...compareData);
            }
          }
        });
      });

      solutionsData.forEach((solutionData) => {
        if (solutionData.dataForGraph) {
          series.push({
            type: 'line',
            name: solutionData.displayName,
            data: solutionData.dataForGraph
          });
        }
      });

      this.chartOptions.xAxis = {
        tickInterval: 360 / this.selectedIndicators.length,
        min: 0,
        max: 360,
        labels: {
          formatter: function () {
            let indicatorIndex = Math.round(Number(this.value) / (this.axis as any).tickInterval);
            return componentContext.labelI18n.indicatorLabelWithUnit(componentContext.selectedIndicators[indicatorIndex]);
          }
        }
      };
    } else {
      let solutionsData: Solution[] = [];

      this.selectedIndicators.forEach((selectedIndicator, i) => {
        compareData = [];

        try {
          let solutionsForIndicator = this.synthese.find((row: any) => row.code === selectedIndicator.code).solutions;
        } catch (error) {
          console.log('Error while finding solutions for indicator: ' + selectedIndicator.code, error);
        }
        let solutionsForIndicator = this.synthese.find((row: any) => row.code === selectedIndicator.code).solutions;
        solutionsForIndicator = solutionsForIndicator.filter((row: any) => row.displayName != 'Comparaison / Base');

        solutionsForIndicator.forEach((solution: any) => {
          let ceuValueForStructureType = 0;
          ceuValueForStructureType = solution.rows[0].values.TotalCost;
          let solutionRef = solutionsData.find((solutionData) => solutionData.id == solution.solutionId);
          compareData.push(ceuValueForStructureType);

          if (solutionRef == null) {
            solutionsData.push({
              id: solution.solutionId,
              displayName: solution.displayName,
              dataForGraph: [ceuValueForStructureType]
            });
          } else {
            solutionRef.dataForGraph?.push(ceuValueForStructureType);
          }
        });

        solutionsData.forEach((solutionData) => {
          if (solutionData.dataForGraph != null) {
            if (solutionData.dataForGraph[i] == Math.max(...compareData)) {
              solutionData.dataForGraph[i] = 100;
            } else {
              solutionData.dataForGraph[i] = 100 + (100 * (solutionData.dataForGraph[i] - Math.max(...compareData))) / Math.max(...compareData);
            }
          }
        });
      });
      solutionsData.forEach((solutionData) => {
        if (solutionData.dataForGraph) {
          series.push({
            type: 'line',
            name: solutionData.displayName,
            data: solutionData.dataForGraph
          });
        }
      });

      this.chartOptions.xAxis = {
        tickInterval: 360 / this.selectedIndicators.length,
        min: 0,
        max: 360,
        labels: {
          formatter: function (this, ctx) {
            let indicatorIndex = Math.round(Number(this.value) / (this.axis as any).tickInterval);
            return componentContext.labelI18n.indicatorLabelWithUnit(componentContext.selectedIndicators[indicatorIndex]);
          }
        }
      };
    }

    this.chartOptions.plotOptions = {
      series: {
        pointStart: 0,
        pointInterval: 360 / this.selectedIndicators.length
      },
      column: {
        pointPadding: 0,
        groupPadding: 0
      }
    };

    this.chartOptions.series = series as Highcharts.SeriesOptionsType[];
    this.updateFlag = true;
  }
}
